import React, { useEffect, useMemo, useState } from "react";
import {
	cloudColumns as baseColumns,
	Cloud,
	shapeCloudData,
} from "./CloudConnectivityData";
import { useNavigate } from "react-router-dom";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericCard from "@ui-components/Card/GenericCard";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchCloudList } from "./requests/request";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { makeCloudFields } from "./cloudFieldSpec";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";

interface MainCloudConnectivity {
	text: string;
}

const MainCloudConnectivity: React.FC = () => {
	const [isFetching, setIsFetching] = useState(false);
	const [cloudList, setCloudList] = useState<Cloud[]>([]);
	const overlaySpinner = useProcessingOverlay(isFetching);
	const navigate = useNavigate();
	const { getAccessTokenSilently } = useAuth0();
	const { tenantId, partnerId } = useAppSelector(state => state.selection);
	const isViewingAllCustomers = tenantId === partnerId;
	const api = useAxiosAuth0();
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [hasFetched, setHasFetched] = useState(false);
	const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

	const cloudFields = useMemo(
		() => makeCloudFields(isViewingAllCustomers),
		[isViewingAllCustomers]
	);

	const normalized = useMemo(
	() => normalizeFilters<Cloud>(activeFilters, cloudFields, {
		defaultStringToContains: true,   // strings default to contains
		quotedStringsExact: true,        // quotes => exact
	}),
	[activeFilters, cloudFields]
	);

	const predicate = useMemo(
	() => buildPredicate<Cloud>(normalized, cloudFields),
	[normalized, cloudFields]
	);

	const filteredData = useMemo(
	() => cloudList.filter(predicate),
	[cloudList, predicate]
	);

	const suggestedKeys = useMemo(
	() => cloudFields.map(f => f.label),
	[cloudFields]
	);


	// ------------------------------------------------------------------------------------
	// Override "Actions"
	// ------------------------------------------------------------------------------------
	// const columns = baseColumns.map((col) => {
	const columns = useMemo<GenericTableColumn<Cloud>[]>(() => {
				const cols = [...baseColumns];  
				if (isViewingAllCustomers) {
					cols.splice(0, 0, {
						header: "Customer",
						accessor: "customer",
						sortAccessor: (row: Cloud) => row.customer?.name ?? "",
						renderCell: (row: Cloud) =>
							row.customer ? (
							<div className="flex flex-col">
								<span className="font-medium">{row.customer.name}</span>
								<span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
							</div>
							) : (
							"—"
							),
					});
				}
				return cols.map(col => {
		// 1. Override the "Service ID" column to navigate on click
		if (col.header === "Connection ID") {
			return {
				...col,
				renderCell: (row: Cloud) => (
					<button
						onClick={() => {
							// CASE A: Partner view (showing ALL customers)
							//   → pick row.customerId (e.g. 11) instead of tenantId
							// CASE B: Customer view (only seeing your own)
							//   → use the Redux‐provided tenantId
							
							const nextTenantId = isViewingAllCustomers
							? Number(row.customer?.id)
							: tenantId;
							navigate(
							`/kontrol/cloud-connectivity/details?connectionId=${row.id}`,
							{
								state: {
								connectionDetail: row,
								tenantOverride: nextTenantId,
								},
							}
							);
						}}
						className="
						hover:text-semantic-text-brand 
						hover:underline
						focus:underline 
						active:underline
						font-medium
						underline
						text-semantic-text-brand"
						>
						{row.subscriptionId}
					</button>
				),
			};
		}

		// 2. Override the “Parent connection” column to navigate on click
		if (col.header === "Parent Connection") {
			return {
			...col,
			renderCell: (row: Cloud) => {
				const subidMatch = row.parentConnection?.match(/\[(PORT|LAG)\](.*)/);
				const subid = subidMatch ? subidMatch[2] : null;
			return (
				<button
				onClick={() => {
					navigate(
					`/kontrol/port-details?portId=${row.parentConnectionId}&subid=${subid}`, {
					state: {
						portType: row.kontrolEdgePort?.portType as 'physical' | 'virtual' | undefined,
					},
				});
				}}
				className="
					hover:text-semantic-text-brand 
					hover:underline
					focus:underline 
					active:underline
					font-medium
					underline
					text-semantic-text-brand
				"
				>
				{row.parentConnection}
				</button>
			);
			},
		};
		}
		return col;
	});
		}, [baseColumns, isViewingAllCustomers]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsFetching(true);
				const response = await fetchCloudList(api, tenantId);
				const { status, data } = response;
				if (status >= 500 || data.success === false) {
					setFetchError(data.message || "Unknown server error");
					setCloudList([]);
				}
				//Valid array payload
				else if (Array.isArray(data.data)) {
					setCloudList(shapeCloudData(data.data));
				}
				//Some other unexpected shape
				else {
					setCloudList([]);
				}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				console.error("Error fetching table cloud connections", err);
				setFetchError(err.message || "Network error");
				setCloudList([]);
			} finally {
			setIsFetching(false);
			setHasFetched(true);
			}
		};
		fetchData();
	}, [getAccessTokenSilently, tenantId]);


	//During the fetching
	if (!hasFetched || isFetching) {
		return overlaySpinner;
	}
	//If there's an error in the request
	if (fetchError) {
		return <ErrorFetchingItems text={fetchError} />;
	}
	// If no data, show empty state
	if (cloudList.length === 0) {
		return <EmptyItems />;
	}

	return (
		<div className="relative">
			<div className="pb-6">
				<GenericCard
					isCollapsible={false}
				>
					<div className="w-[520px] m-5">
						<GenericSearchbar
						placeholder='Find by attribute or value'
						suggestedKeys={suggestedKeys}
						onFiltersChange={setActiveFilters}
						liveFiltering
						debounceMs={200}
						/>
					</div>
					<div className="px-5 mb-5">
						<GenericTable
							columns={columns}
							data={filteredData}
							initialRowsPerPage={10}
							rowsPerPageOptions={[10, 20, 30]}
							width="100%"
							tableClassName="bg-semantic-bg-primary"
						/>
					</div>
				</GenericCard>
			</div>
		</div>
	);
};

export default MainCloudConnectivity;
